import React, { useState, useEffect, useRef } from "react";
import "./LoginTime.css";
import ExcelJS from "exceljs";

import Select from "react-select";
import axios from "axios";
import jsPDF from "jspdf";
import "jspdf-autotable";
import { selectStyles } from '../../../assets/Styles/selectStyle';
import { getDaysInMonth } from "../../../utils/calculateDaysInMonthUtils";
import { capitalizeFirstLetter, timeConverter } from "../../../utils/validations";
import DatePicker from 'react-datepicker';
import { getDistrict, getStates, fetchMonths, getLoginTimeResponse, getDistrictByZoneId, getMonthlyLoginTimeReport } from '../../../services/monthlyAttendanceService';

import { getDesignationsPriority } from "../../../services/employeeCreationService";


const LoginTime = () => {

    const empRole = localStorage.getItem('role');
    const distctLoginUser = localStorage.getItem('districtId');
    const loggedInDistrictName = localStorage.getItem('districtName');
    const loggedInStateId = localStorage.getItem('stateId');
    const loggedInStateName = localStorage.getItem('stateName');
    const designationName = localStorage.getItem('designationName');
    const zoneId = localStorage.getItem("zoneId");

    const [state, setState] = useState(3);

    const [district, setDistrict] = useState(() => {
        // Set initial district based on role
        if (empRole === "ITM") {
            return distctLoginUser;
        }
        return null;
    });

    const [districtName, setDistrictName] = useState(loggedInDistrictName);
    const effectRan = useRef(false);
    const [month, setMonth] = useState(new Date().getMonth() + 1);
    const [year, setYear] = useState(new Date().getFullYear());
    const [stateId, setStateId] = useState(null);
    const [districtId, setDistrictId] = useState(null);
    const [monthOptions, setMonthOptions] = useState([]);
    const [stateOptions, setStateOptions] = useState([]);
    const [districtOptions, setDistrictOptions] = useState([]);
    const [attendanceData, setAttendanceData] = useState([]);
    const [selectedMonthYear, setSelectedMonthYear] = useState(null);

    const isFetchDataEnabled = state && district && month && year;
    const today = new Date();
    const currentDay = today.getDate();
    const currentMonth = today.getMonth() + 1;
    const currentYear = today.getFullYear();
    const minDate = new Date(2026, 0, 1);
    const maxDate = new Date(currentYear, 11, 31);
    const [selectedDesignation, setSelectedDesignation] = useState(null);
    const [designations, setDesignations] = useState([]);

    const [isFocused, setIsFocused] = useState({
        state: false,
        district: false,
        month: false,
        year: false,
        designations: false,
    });
    const [errors, setErrors] = useState({});
    const yearInputRef = useRef(null);
    const days = month && year ? getDaysInMonth(month, year) : 0;


    // Fetch states
    useEffect(() => {
        const fetchStates = async () => {
            try {
                const data = await getStates();

                const sortedStates = data
                    .sort((a, b) => a.stateName.localeCompare(b.stateName)) // Alphabetical sort
                    .map(item => ({
                        value: item.id,
                        label: capitalizeFirstLetter(item.stateName),
                    }));

                setStateOptions(sortedStates);
            } catch (error) {
                console.error('Error fetching states:', error);
                setErrors(prev => ({ ...prev, general: 'Failed to load states' }));
            }
        };

        if (!effectRan.current) {
            fetchStates();
        }
        return () => {
            effectRan.current = true;
        };
    }, []);


    useEffect(() => {
        const loadMonths = async () => {
            try {
                const months = await fetchMonths(year);

                // ✅ Ensure current month is included in the list
                const today = new Date();
                const currentMonthValue = today.getMonth() + 1; // 1-based
                const monthExists = months.some(m => m.value === currentMonthValue);
                if (!monthExists && year === today.getFullYear()) {
                    months.push({ value: currentMonthValue, label: today.toLocaleString('default', { month: 'long' }) });
                    months.sort((a, b) => a.value - b.value); // optional, to keep months in order
                }

                setMonthOptions(months);
            } catch (error) {
                console.error("Error fetching months:", error);
                setErrors(prev => ({ ...prev, general: "Failed to load months" }));
            }
        };
        loadMonths();
    }, [year]);


    // Fetch districts when state changes
    useEffect(() => {
        const fetchDistricts = async () => {
            try {
                let data = null;

                // Check if user has PM, APM_OPERATIONS, or ADMIN role
                const isPMOrAdmin = ["PM", "APM_OPERATIONS", "ADMIN"].includes(empRole);
                const isAPM_Zone = ["APM_Zone"].includes(empRole);

                if (isAPM_Zone && zoneId) {
                    // For PM, APM_OPERATIONS, ADMIN - use zone API
                    const response = await getDistrictByZoneId(zoneId);
                    if (response?.districts) {
                        data = response.districts;
                    }
                } else if (state) {
                    // For other roles (ITM, etc.) - use state API
                    data = await getDistrict(state);
                }

                if (data && Array.isArray(data)) {
                    const sortedDistricts = data
                        .sort((a, b) => a.districtName.localeCompare(b.districtName))
                        .map(item => ({
                            value: item.districtId,
                            label: item.districtName,
                        }));

                    setDistrictOptions(sortedDistricts);

                    // Auto-select district for ITM role
                    if (empRole === "ITM" && distctLoginUser) {
                        const matchedDistrict = sortedDistricts.find(d => d.value === parseInt(distctLoginUser));
                        if (matchedDistrict) {
                            setDistrict(matchedDistrict.value);
                        }
                    }
                } else {
                    setDistrictOptions([]);
                }
            } catch (error) {
                console.error('Error fetching districts:', error);
                setErrors(prev => ({ ...prev, general: 'Failed to load districts' }));
                setDistrictOptions([]);
            }
        };

        // Trigger fetch based on conditions
        if (["PM", "APM_OPERATIONS", "ADMIN"].includes(empRole) && zoneId) {
            fetchDistricts();
        } else if (state) {
            fetchDistricts();
        } else {
            setDistrictOptions([]);
            if (empRole !== "ITM") {
                setDistrict(null);
            }
        }
    }, [state, empRole, zoneId, distctLoginUser]);



    // Fetch designations
    useEffect(() => {
        const fetchDesignation = async () => {
            try {
                const data = await getDesignationsPriority();
                if (Array.isArray(data)) {

                    const sortedData = data
                        .sort((a, b) => a.designationName.localeCompare(b.designationName))
                        .map((d) => ({
                            value: d.id,
                            label: d.designationName,
                        }));

                    setDesignations(sortedData);
                } else {
                    console.error("Invalid response: designations should be an array");
                }
            } catch (err) {
                console.error("Error fetching designation:", err);
            }
        };

        if (effectRan.current === false) {
            fetchDesignation();
        }
        return () => {
            effectRan.current = true;
        };
    }, []);


    const handleYearChange = (date) => {
        setYear(date ? date.getFullYear() : null);
        setErrors(prev => ({ ...prev, year: null }));
    };

    const isCurrentMonth =
        (!selectedMonthYear &&
            today.getMonth() + 1 === currentMonth &&
            today.getFullYear() === currentYear) ||
        (selectedMonthYear &&
            parseInt(selectedMonthYear.value.split("-")[1]) === currentMonth &&
            parseInt(selectedMonthYear.value.split("-")[0]) === currentYear);


    const handleInputChange = (selectedOption, field) => {
        const value = selectedOption ? selectedOption.value : null;
        switch (field) {
            case 'state':
                setState(value);
                setStateId(value);
                setDistrict(null);
                setMonth(null);

                break;
            case 'district':
                setDistrict(value);
                setDistrictId(value);
                break;
            case 'month':
                setMonth(value);
                break;
            case 'designations':
                setSelectedDesignation(value);
                break;

            default:
                break;
        }
        setErrors(prev => ({ ...prev, [field]: null }));
    };
    const handleSubmit = async (e) => {
        e.preventDefault();

        try {
            // const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/attendance/monthlylogintime-report?stateId=${state}&districtId=${district}&year=${year}&month=${month}`);
            // const data = await response.json();
            const data = await getMonthlyLoginTimeReport(state, district, year, month);
            console.log("API Response:", data); // For debugging

            // Transform the new API response structure
            const transformedData = data.employees.map((employee) => {
                const dayWiseStatus = {};

                // Process attendance days for all days in the month
                for (let day = 1; day <= data.totalDays; day++) {
                    const dayValue = employee.attendanceDays[day];

                    if (dayValue) {
                        // Format the value: if it's a time, format to HH:MM, otherwise show as is (H, WO, A, etc.)
                        if (dayValue !== "H" && dayValue !== "WO" && dayValue !== "A" && dayValue !== "-" && dayValue.includes(":")) {
                            // Format time to HH:MM (remove seconds)
                            const timeParts = dayValue.split(":");
                            if (timeParts.length >= 2) {
                                dayWiseStatus[day] = `${timeParts[0]}:${timeParts[1]}`;
                            } else {
                                dayWiseStatus[day] = dayValue;
                            }
                        } else {
                            dayWiseStatus[day] = dayValue;
                        }
                    } else {
                        dayWiseStatus[day] = "-";
                    }
                }

                // Calculate statistics
                let present = 0;
                let absent = 0;
                let holiday = 0;
                let weekOff = 0;

                for (let day = 1; day <= data.totalDays; day++) {
                    const value = dayWiseStatus[day];
                    if (value && value !== "-" && value !== "H" && value !== "WO" && value !== "A") {
                        present++;
                    } else if (value === "A") {
                        absent++;
                    } else if (value === "H") {
                        holiday++;
                    } else if (value === "WO") {
                        weekOff++;
                    }
                }

                return {
                    employeeId: employee.employeeUniqueId,
                    fullName: employee.employeeName,
                    designation: employee.designation,
                    contactNumber: employee.contactNumber,
                    districtName: employee.assignedDistrict || loggedInDistrictName || "N/A",
                    dayWiseStatus: dayWiseStatus,
                    presentDays: present,
                    absentDays: absent,
                    holidayDays: holiday,
                    weekOffDays: weekOff,
                    payableDays: present + holiday + weekOff,
                };
            });

            setAttendanceData(transformedData);
            //setApiResponse(data); // Store for reference if needed

        } catch (error) {
            console.error("Error fetching attendance data:", error);
            setErrors(prev => ({ ...prev, general: 'Failed to load attendance data' }));
        }
    };
    // Determine if state field should be shown (hide for PM, APM_OPERATIONS, ADMIN)
    const showStateField = !["PM", "APM_OPERATIONS", "ADMIN"].includes(empRole);

    // Determine if district should be disabled for ITM
    const isDistrictDisabled = empRole === "ITM";

    // 🔹 Excel Download
    const generateExcel = async () => {
        const workbook = new ExcelJS.Workbook();
        const worksheet = workbook.addWorksheet("Attendance");

        // Get filter labels
        const stateLabel = stateOptions.find(opt => opt.value === state)?.label || "";
        const districtLabel = districtOptions.find(opt => opt.value === district)?.label || "";
        const monthYearLabel = `${monthOptions.find(opt => opt.value === month)?.label || "N/A"}-${year || "N/A"}`;


        // Title
        worksheet.mergeCells("A1:AJ1");
        const titleCell = worksheet.getCell("A1");
        titleCell.value = "District Employees Login Report";
        titleCell.font = { bold: true, size: 16 };
        titleCell.alignment = { horizontal: "center" };

        // Filters in merged row
        worksheet.mergeCells("A2:AJ2");
        const filterCell = worksheet.getCell("A2");
        filterCell.value = `State: ${stateLabel}   |   District: ${districtLabel}   |   Month-Year: ${monthYearLabel}`;
        filterCell.alignment = { horizontal: "left" }; // make it centered across merged cells
        filterCell.font = { bold: true, size: 12 };

        // Table headers
        const headers = [
            "S.No",
            "District",
            // "EmployeeId",
            "Employee Name",
            "Designation",
            "Contact Number",
            ...Array.from({ length: days }, (_, i) => `Day-${i + 1}`),
            // "Present",
            // "Casual Leave",
            // "Absent",
            // "Total No.of Days Payable",
        ];

        worksheet.addRow(headers);

        // Style headers
        const headerRow = worksheet.getRow(worksheet.lastRow.number);
        headerRow.eachCell((cell) => {
            cell.font = { bold: true, color: { argb: "FFFFFFFF" } };
            cell.fill = {
                type: "pattern",
                pattern: "solid",
                fgColor: { argb: "FF4F81BD" },
            };
            cell.alignment = { horizontal: "center", vertical: "middle", wrapText: true };
            cell.border = {
                top: { style: "thin" },
                left: { style: "thin" },
                bottom: { style: "thin" },
                right: { style: "thin" },
            };
        });

        // Add rows from attendanceData
        attendanceData.forEach((emp, index) => {
            const row = [
                index + 1,
                emp.districtName || "",
                // emp.employeeId || "",
                emp.fullName || "",
                emp.designation || "",
                emp.contactNumber || "-",
                ...Array.from({ length: days }, (_, i) => {
                    const day = (i + 1).toString();
                    let status = emp.dayWiseStatus?.[day] || "-";

                    // Don't modify the status - keep original values (time, H, WO, A)
                    // Just ensure empty values become "-"
                    if (!status || status === "" || status === null || status === undefined) {
                        status = "-";
                    }

                    return timeConverter(status);
                }),
                // emp.presentDays || 0,
                // emp.casualLeaveDays || 0,
                // emp.absentDays || 0,
                // emp.payableDays || 0,
            ];
            worksheet.addRow(row);
        });

        // Auto adjust column widths
        worksheet.columns.forEach((column, index) => {
            let maxLength = 7;
            column.eachCell({ includeEmpty: true }, (cell) => {
                const columnLength = cell.value ? cell.value.toString().length : 5;
                if (columnLength > maxLength) maxLength = columnLength;
            });

            if (index < 5) {
                // Limit first 4 columns to max width 20
                column.width = Math.min(maxLength + 3, 20);
            } else if (index >= 4 && index < 4 + days) {
                // Day columns → fixed small width
                column.width = 10;
            } else {
                // Summary columns → auto width
                column.width = Math.min(maxLength + 3, 15);
            }
        });


        // Download Excel
        const buffer = await workbook.xlsx.writeBuffer();
        const blob = new Blob([buffer], { type: "application/octet-stream" });
        const link = document.createElement("a");
        link.href = URL.createObjectURL(blob);
        link.download = `Login_Time_Report_${monthYearLabel}.xlsx`;
        link.click();
    };


    const generatePDF = () => {
        // if (attendanceData.length === 0) {
        //   alert("No data to export");
        //   return;
        // }

        const doc = new jsPDF("l", "pt", "a3"); // Landscape, bigger size (A3) for many columns

        // Title
        doc.setFontSize(16);
        doc.text("Daily Attendance Report", 40, 30);

        // Get selected filters
        const stateLabel = stateOptions.find(opt => opt.value === state)?.label || "";
        const districtLabel = districtOptions.find(opt => opt.value === district)?.label || "";

        const monthYearLabel = `${monthOptions.find(opt => opt.value === month)?.label || "N/A"}-${year || "N/A"}`;


        // Print filters
        doc.setFontSize(11);
        doc.text(`State: ${stateLabel}`, 40, 50);
        doc.text(`District: ${districtLabel}`, 250, 50);
        doc.text(`Month-Year: ${monthYearLabel}`, 40, 65);

        // Dynamic columns: District, EmployeeId, Employee Name, Designation, days..., Present, CL, Absent, Payable
        const tableColumn = [
            "S.No",
            "District",
            "EmployeeId",
            "Employee Name",
            "Designation",
            ...Array.from({ length: days }, (_, i) => `${i + 1}`),
            // "Present",
            // "Absent",
            // "Total No.of Days Payable",
        ];

        // Table rows
        const tableRows = attendanceData.map((emp, index) => {
            const row = [
                index + 1,
                emp.districtName || "",
                emp.employeeId || "",
                emp.fullName || "",
                emp.designation || "",
                ...Array.from({ length: days }, (_, i) => {
                    const day = (i + 1).toString();
                    const status = emp.dayWiseStatus?.[day] || "";

                    // ✅ Convert N / NA / Not Marked / null to blank
                    return ["N", "NA", "Not Marked", null, "", undefined].includes(status)
                        ? "-"
                        : status.charAt(0);
                }),
                // emp.presentDays || 0,
                // emp.casualLeaveDays || 0,
                // emp.absentDays || 0,
                // emp.payableDays || 0,
            ];
            return row;
        });

        // Generate table
        doc.autoTable({
            head: [tableColumn],
            body: tableRows,
            startY: 80,
            styles: { fontSize: 7 }, // smaller font to fit
            headStyles: { fillColor: [79, 129, 189], textColor: 255 },
            margin: { left: 40, right: 40 },
            tableWidth: "auto",
        });

        doc.save(`Monthly_Attendance_${monthYearLabel}.pdf`);
    };


    return (
        <div className="login-time-main">
            <div className="login-time-head">
                {/* <h5>Daily Attendance Report</h5> */}
                <div className="login-time-box">
                    <h2 className="login-time-heading">District Employees Login Report</h2>
                    {errors.general && <div className="error-message">{errors.general}</div>}
                    <form onSubmit={handleSubmit} >
                        <div className="login-time-maincontainer">
                            <div className="login-time-container-part">
                                <div className={`login-time-floating-label-container ${isFocused.state || state ? 'float-label' : ''}`}>
                                    <Select
                                        name="state"
                                        options={stateOptions}
                                        value={stateOptions.find(opt => opt.value === state) || null}
                                        onChange={option => handleInputChange(option, 'state')}
                                        onFocus={() => setIsFocused(prev => ({ ...prev, state: true }))}
                                        onBlur={() => setIsFocused(prev => ({ ...prev, state: false }))}
                                        placeholder=" "
                                        isClearable
                                        menuPortalTarget={document.body}
                                        styles={selectStyles}

                                    />
                                    <label><span className="required">*</span>State</label>
                                    {errors.state && <div className="error-message">{errors.state}</div>}
                                </div>
                            </div>
                            <div className="login-time-container-part">
                                <div className={`login-time-floating-label-container ${isFocused.district || district ? 'float-label' : ''}`}>
                                    <Select
                                        name="district"
                                        options={districtOptions}
                                        value={districtOptions.find(opt => opt.value === district) || null}
                                        onChange={option => handleInputChange(option, 'district')}
                                        onFocus={() => setIsFocused(prev => ({ ...prev, district: true }))}
                                        onBlur={() => setIsFocused(prev => ({ ...prev, district: false }))}
                                        placeholder=" "
                                        isClearable={!isDistrictDisabled}
                                        menuPortalTarget={document.body}
                                        styles={selectStyles}
                                        isDisabled={isDistrictDisabled}
                                    />

                                    <label><span className="required">*</span>District</label>
                                    {errors.district && <div className="error-message">{errors.district}</div>}
                                </div>
                            </div>
                        </div>
                        <div className="login-time-maincontainer">
                            <div className="login-time-container-part">
                                <div className={`login-time-floating-label-container ${isFocused.year || year ? 'float-label' : ''}`}>
                                    <DatePicker
                                        selected={year ? new Date(year, 0, 1) : null}
                                        onChange={handleYearChange}
                                        onFocus={() => setIsFocused(prev => ({ ...prev, year: true }))}
                                        onBlur={() => setIsFocused(prev => ({ ...prev, year: false }))}
                                        dateFormat="yyyy"
                                        showYearPicker
                                        placeholderText=""
                                        className="login-time-date-picker"
                                        minDate={minDate}
                                        maxDate={maxDate}
                                        ref={yearInputRef}
                                        title="Select a year"
                                    />
                                    <label><span className="required">*</span>Year</label>
                                    {errors.year && <div className="error-message">{errors.year}</div>}
                                </div>
                            </div>
                            <div className="login-time-container-part">
                                <div className={`login-time-floating-label-container ${isFocused.month || month ? 'float-label' : ''}`}>
                                    <Select
                                        name="month"
                                        options={monthOptions}
                                        value={monthOptions.find(opt => opt.value === month) || null}
                                        onChange={option => handleInputChange(option, 'month')}
                                        onFocus={() => setIsFocused(prev => ({ ...prev, month: true }))}
                                        onBlur={() => setIsFocused(prev => ({ ...prev, month: false }))}
                                        placeholder=" "
                                        isClearable
                                        menuPortalTarget={document.body}
                                        styles={selectStyles}
                                    />
                                    <label><span className="required">*</span>Month</label>
                                    {errors.month && <div className="error-message">{errors.month}</div>}
                                </div>
                            </div>

                        </div>

                        {/* <div className="login-time-maincontainer">
                            <div className="login-time-container-part">
                                <div className={`login-time-floating-label-container ${isFocused.selectedDesignation || selectedDesignation ? 'float-label' : ''}`}>
                                    <Select
                                        options={designations}
                                        value={designations.find((option) => option.value === selectedDesignation) || null}
                                        onChange={(selectedOption) => handleInputChange(selectedOption, 'designations')}
                                        placeholder=""
                                        isClearable
                                        menuPortalTarget={document.body}
                                        // styles={{
                                        //     menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                                        //     control: (provided) => ({
                                        //         ...provided,
                                        //         backgroundColor: "#fff",
                                        //         borderColor: "#c9c9c9",
                                        //     }),
                                        //     singleValue: (provided) => ({ ...provided, color: "#474747" }),
                                        // }}
                                        styles={selectStyles}
                                    />
                                    <label>Designation</label>
                                  
                                </div>
                            </div>
                            <div className="login-time-container-part">

                            </div>
                        </div> */}
                        <div className="login-time-btn-div">
                            <button type="submit" className="login-time-btn"
                                disabled={!isFetchDataEnabled}

                            >
                                Generate Report
                            </button>
                        </div>
                    </form>
                </div>


                <div className="daily-gen-button">
                    {/* <button
                        className="login-time-downloadexcel"
                        onClick={generatePDF}
                        disabled={attendanceData.length === 0} 
                    >
                        Download PDF
                    </button> */}
                    <button
                        className="login-time-downloadexcel"
                        onClick={generateExcel}
                        disabled={attendanceData.length === 0} // ✅ Disable if no data
                    >
                        Download Excel
                    </button>
                </div>

                <div className="login-time-div">


                    <div className="login-time-wrapper">
                        <table className="login-time-table">
                            <thead>
                                <tr>
                                    <th>S.No</th>
                                    <th>District</th>
                                    {/* <th>EmployeeId</th> */}
                                    <th>Employee Name</th>
                                    <th>Designation</th>
                                    <th>Contact Number</th>

                                    {Array.from({
                                        length: getDaysInMonth(month || currentMonth, year || currentYear)
                                    }, (_, i) => {
                                        const dayNum = i + 1;

                                        const defaultMonth = month || currentMonth;
                                        const defaultYear = year || currentYear;

                                        const isPast = defaultMonth === currentMonth && defaultYear === currentYear && dayNum < currentDay;
                                        const isToday = defaultMonth === currentMonth && defaultYear === currentYear && dayNum === currentDay;

                                        return (
                                            <th
                                                key={dayNum}
                                                // style={{
                                                //     backgroundColor: isToday ? "lightgreen" : undefined,
                                                //     color: isPast ? "gray" : undefined,
                                                //     pointerEvents: isPast ? "none" : "auto",
                                                // }}
                                                className=" text-nowrap"
                                            >
                                                Day-{dayNum}
                                            </th>
                                        );
                                    })}

                                    {/* <th>Present</th> */}
                                    {/* <th>Casual Leave</th> */}
                                    {/* <th>Absent</th> */}
                                    {/* <th>Total No.of Days Payable</th> */}
                                </tr>
                            </thead>

                            <tbody>
                                {attendanceData.map((employee, index) => (
                                    <tr key={employee.employeeId}>
                                        <td>{index + 1}</td>
                                        <td>{employee.districtName}</td>
                                        {/* <td>{employee.employeeId}</td> */}
                                        <td>{employee.fullName}</td>
                                        <td>{employee.designation}</td>
                                        <td>{employee.contactNumber || "-"}</td>
                                        {Array.from({ length: days }, (_, dayIndex) => {
                                            const day = dayIndex + 1;
                                            const value = employee.dayWiseStatus[day] || "-";

                                            // Add styling classes based on value type
                                            let cellClass = "";
                                            if (value === "H") cellClass = "holiday-cell";
                                            else if (value === "WO") cellClass = "weekoff-cell";
                                            else if (value === "A") cellClass = "absent-cell";
                                            else if (value !== "-" && value !== "H" && value !== "WO" && value !== "A") cellClass = "login-time-cell";

                                            return (
                                                <td key={dayIndex} className={cellClass}>
                                                    {timeConverter(value)}
                                                </td>
                                            );
                                        })}
                                    </tr>
                                ))}
                            </tbody>

                        </table>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default LoginTime;
